import {
  ROLES_ARVAL,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_PARTNER_READ_ONLY,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  useAuth,
} from 'auth'
import getDisabledChoices from 'lib/getCarDisabledChoices'
import getPictures from 'lib/getCarPictures'
import { getCarContextKey, getSessionIdFromCar } from 'lib/carContextKey'
import { isRepairSession } from 'lib/sessionLifecycle'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CarDetails from 'components/organisms/CarDetails'
import CarsToBuyCarDetailsRightBottom from 'components/organisms/CarsToBuy/CarsToBuyCarDetailsRightBottom'
import {
  getBlindOffersRemainingByType,
  normalizeBlindOffersRemaining,
} from 'components/organisms/CarsToBuy/blindOffersLib'

import CarDetailsPageTitle from '../CarDetails/CarDetailsPageTitle'

function resolveActiveCarKey(
  detailsContextKey,
  plate,
  detailsSessionId,
  sessionId,
  fallbackSessionId
) {
  return (
    detailsContextKey ||
    getCarContextKey(
      { plate, sessionId: detailsSessionId || sessionId || null },
      fallbackSessionId
    )
  )
}

async function applyBlindSelection(
  car,
  sessionId,
  myPartnerId,
  updateChoice,
  setBlindSelectionByPlate,
  setSelectionByPlate
) {
  if (car.firstChoice) {
    const success = await updateChoice(
      sessionId,
      myPartnerId,
      car.plate,
      'none'
    )
    if (!success) {
      return false
    }
  }
  const contextKey = getCarContextKey(car, sessionId)
  if (!contextKey) {
    return false
  }
  setBlindSelectionByPlate((current) => ({ ...current, [contextKey]: true }))
  setSelectionByPlate((current) => ({ ...current, [contextKey]: 'blind' }))
  return true
}

export default function CarsToBuyCarDetails({
  partnerId,
  plate,
  detailsContextKey,
  detailsSessionId,
  session,
  isFetching,
  updateChoice,
  setReprice,
  setBlindPrice = () => {},
  blindSessionId,
  toggleFavorite,
  cars,
  isFetchingCars,
  isUpdatingCars,
  selectedCarsToBuy,
  setShowDetails,
  allCars,
  isBlindPublication = false,
  showBlindPrice = false,
  blindReadOnly = false,
  blindOffersRemaining,
  blindSelectionByPlate = {},
  setBlindSelectionByPlate = () => {},
  selectionByPlate = {},
  setSelectionByPlate = () => {},
}) {
  const { t } = useTranslation()
  const { getPartnerId } = useAuth()
  const { hasOneOfRoles } = useAuth()
  const admin = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () =>
      hasOneOfRoles([
        ROLE_READ_ONLY,
        ROLE_LOGISTICS,
        ROLE_B2C,
        ROLE_PARTNER_READ_ONLY,
        ROLE_POST_SALES,
      ]),
    [hasOneOfRoles]
  )

  const myPartnerId = useMemo(
    () => (admin ? partnerId : getPartnerId() || partnerId),
    [admin, getPartnerId, partnerId]
  )

  const detailsFallbackSessionId =
    session?.sessionId || detailsSessionId || null

  const readOnly = useMemo(
    () => isFetchingCars || isFetching || isUpdatingCars,
    [isFetchingCars, isFetching, isUpdatingCars]
  )

  const car = useMemo(() => {
    if (!plate) {
      return null
    }

    const toMatchPlate = plate.toLowerCase()
    if (detailsContextKey) {
      const byContext = cars.find(
        (item) =>
          getCarContextKey(item, detailsFallbackSessionId) === detailsContextKey
      )
      if (byContext) {
        return byContext
      }
    }

    return (
      cars.find((item) => item.plate.toLowerCase() === toMatchPlate) || null
    )
  }, [cars, detailsContextKey, detailsFallbackSessionId, plate])

  const pictures = useMemo(
    () => getPictures(car?.pictureurl),
    [car?.pictureurl]
  )

  const caps = useMemo(
    () =>
      (isBlindPublication ? session.partnerBlindCaps : session.partnerCaps) ||
      {},
    [isBlindPublication, session]
  )

  const carsForCaps = useMemo(
    () => (allCars?.length ? allCars : cars),
    [allCars, cars]
  )

  const resolvedBlindOffersRemaining = useMemo(() => {
    const hasBlindOffersRemaining =
      blindOffersRemaining != null && blindOffersRemaining !== ''
    if (hasBlindOffersRemaining || !isBlindPublication) {
      return normalizeBlindOffersRemaining(blindOffersRemaining)
    }

    return getBlindOffersRemainingByType({
      caps,
      cars: carsForCaps,
    })
  }, [blindOffersRemaining, caps, carsForCaps, isBlindPublication])

  const disabledChoices = useMemo(
    () => getDisabledChoices(carsForCaps, caps),
    [caps, carsForCaps]
  )

  const handleConfirmReprice = (plate, reprice) => {
    setReprice(session.sessionId, myPartnerId, plate, reprice)
  }

  const showNonBlindReprice = !!session.canReprice && !isBlindPublication
  const repairSession = isRepairSession(session)
  const blindPriceSessionId =
    blindSessionId || (isBlindPublication ? session.sessionId : null)
  const resolvedShowBlindOption = isBlindPublication || showBlindPrice
  const resolvedShowBlindPrice =
    !admin && resolvedShowBlindOption && !!blindPriceSessionId
  const showBlindPositiveControls = !repairSession
  const showPriceRange = !isBlindPublication && !resolvedShowBlindPrice

  const handleConfirmBlindPrice = async (plate, reprice) => {
    if (!blindPriceSessionId) {
      return
    }
    await setBlindPrice(blindPriceSessionId, myPartnerId, plate, reprice)
    if (reprice != null && reprice !== '') {
      return
    }
    clearBlindSelection(plate)
    const contextKey = getCarContextKey(
      { plate, sessionId: session?.sessionId },
      session?.sessionId
    )
    if (contextKey) {
      setSelectionByPlate((current) => ({
        ...current,
        [contextKey]: 'none',
      }))
    }
  }

  const getSelection = useCallback(
    (car) => {
      const contextKey = getCarContextKey(car, session?.sessionId)
      const hasLocalBlindSelection =
        contextKey && blindSelectionByPlate[contextKey]
      const hasBlindPrice =
        car.blindPrice !== null && car.blindPrice !== undefined

      if (contextKey && selectionByPlate[contextKey]) {
        return selectionByPlate[contextKey]
      }
      if (car.firstChoice) {
        return 'selected'
      }
      if (hasBlindPrice) {
        return 'blind'
      }
      return hasLocalBlindSelection ? 'blind' : 'none'
    },
    [blindSelectionByPlate, selectionByPlate, session?.sessionId]
  )

  const clearBlindSelection = useCallback(
    (plate) => {
      const contextKey = getCarContextKey(
        { plate, sessionId: session?.sessionId },
        session?.sessionId
      )
      if (!contextKey) {
        return
      }

      setBlindSelectionByPlate((current) => {
        if (!current[contextKey]) {
          return current
        }
        const next = { ...current }
        delete next[contextKey]
        return next
      })
    },
    [session?.sessionId, setBlindSelectionByPlate]
  )

  const handleSelectionChange = useCallback(
    async (car, nextSelection) => {
      if (readOnly || userRoleHasReadOnly) {
        return false
      }

      if (nextSelection === 'blind') {
        return applyBlindSelection(
          car,
          session?.sessionId,
          myPartnerId,
          updateChoice,
          setBlindSelectionByPlate,
          setSelectionByPlate
        )
      }

      const choice = nextSelection === 'selected' ? 'first' : 'none'
      const success = await updateChoice(
        session.sessionId,
        myPartnerId,
        car.plate,
        choice
      )
      if (!success) {
        return false
      }

      clearBlindSelection(car.plate)
      const contextKey = getCarContextKey(car, session?.sessionId)
      if (contextKey) {
        setSelectionByPlate((current) => ({
          ...current,
          [contextKey]: nextSelection,
        }))
      }
      if (
        resolvedShowBlindPrice &&
        car.blindPrice !== null &&
        car.blindPrice !== undefined
      ) {
        await setBlindPrice(blindPriceSessionId, myPartnerId, car.plate, null)
      }

      return true
    },
    [
      readOnly,
      userRoleHasReadOnly,
      updateChoice,
      session?.sessionId,
      myPartnerId,
      clearBlindSelection,
      resolvedShowBlindPrice,
      setBlindPrice,
      blindPriceSessionId,
      setSelectionByPlate,
      setBlindSelectionByPlate,
    ]
  )

  const handleToggleFavorite = () => {
    if (car && toggleFavorite) {
      toggleFavorite(session.sessionId, myPartnerId, car.plate, car.favorite)
    }
  }

  return (
    <div className="content-container" data-testid="cars-to-buy-details">
      <>
        <CarDetailsPageTitle
          setShowDetails={setShowDetails}
          cars={cars}
          plate={plate}
          activeCarKey={resolveActiveCarKey(
            detailsContextKey,
            plate,
            detailsSessionId,
            session?.sessionId,
            detailsFallbackSessionId
          )}
          getCarKey={(item) =>
            getCarContextKey(item, detailsFallbackSessionId) || item?.plate
          }
          getShowDetailsValue={(item) => ({
            plate: item?.plate,
            sessionId: getSessionIdFromCar(item) ?? detailsFallbackSessionId,
            contextKey: getCarContextKey(item, detailsFallbackSessionId),
          })}
        />
        {car && (
          <CarDetails
            car={car}
            price={car.price}
            maxPrice={car.maxPrice}
            showPriceRange={showPriceRange}
            pictures={pictures}
            onToggleFavorite={toggleFavorite ? handleToggleFavorite : undefined}
            rightBottom={
              <CarsToBuyCarDetailsRightBottom
                variant="column"
                session={session}
                partnerId={myPartnerId}
                cars={allCars || cars}
                caps={caps}
                car={car}
                disabledChoices={disabledChoices}
                selectedChoices={
                  session.showOtherPartners
                    ? selectedCarsToBuy?.find(
                        (selectedCar) =>
                          getCarContextKey(selectedCar, session?.sessionId) ===
                          getCarContextKey(car, session?.sessionId)
                      )
                    : undefined
                }
                selection={getSelection(car)}
                onSelectionChange={handleSelectionChange}
                handleConfirmReprice={handleConfirmReprice}
                handleConfirmBlindPrice={handleConfirmBlindPrice}
                showNonBlindReprice={showNonBlindReprice}
                showBlindPrice={
                  resolvedShowBlindPrice && getSelection(car) === 'blind'
                }
                readOnly={admin || readOnly || userRoleHasReadOnly}
                showNotice={
                  !['GJ542JB', 'FN144TV', 'FX824EH'].includes(car.plate)
                }
                blindOffersRemaining={resolvedBlindOffersRemaining}
                blindReadOnly={blindReadOnly}
                showBlindOption={resolvedShowBlindOption}
                showBlindPositiveSignal={showBlindPositiveControls}
                showBlindPositiveOffers={showBlindPositiveControls}
                showBlindConfirmButton={!admin}
                repairHintText={
                  repairSession && resolvedShowBlindOption
                    ? t('carsToBuy.repair.publicationHint')
                    : undefined
                }
                showRepairBlindHint={repairSession}
              />
            }
          />
        )}
      </>
    </div>
  )
}
